import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import NumberFormat from "react-number-format"
import moment from "moment"
import { Breadcrumb } from "../Breadcrumb"
import { Model } from "../../../models/model"
import "../EstadoResultado/estadoResultado.css"

const concepts = [
    { key: 'received', label: 'Cobros' },
    { key: 'paid', label: 'Pagos' },
    { key: 'net', label: 'Flujo neto' },
    { key: 'accumulated', label: 'Flujo neto acumulado' },
]
const compact = new Intl.NumberFormat('es-PE', { notation: 'compact', maximumFractionDigits: 1 })

export const FlujoEfectivo = ({ showNav }) => {
    const money = useSelector(state => state.general.info)
    const [period, setPeriod] = useState('3')
    const [refresh, setRefresh] = useState(0)
    const [report, setReport] = useState(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            const query = new URLSearchParams({ period, cash_flow_type: 'flujo_efectivo' })
            const result = await Model.getAll({ url: `reports/cashflow/new/efectivo?${query}` })
            if (active) setReport({ period, refresh, result })
        }
        load()
        return () => { active = false }
    }, [period, refresh])

    const loaded = report?.period === period && report?.refresh === refresh
    const result = loaded ? report.result : null
    const rows = result?.data || []
    const months = (result?.months || []).map(({ start_date }, index) => ({
        label: moment(start_date).locale('es').format('MMM YYYY'),
        ...Object.fromEntries(concepts.map(({ key }, rowIndex) => [key, Number(rows[rowIndex].values[index].value)])),
    }))
    const totals = Object.fromEntries(concepts.map(({ key }, index) => [
        key, Number(rows[index]?.values[months.length]?.value || 0),
    ]))
    const lowest = months.reduce((worst, month) => month.net < (worst?.net ?? 0) ? month : worst, null)

    const amount = value => (
        <NumberFormat displayType="text" value={value}
            thousandSeparator={money?.local_separacion_miles || ','}
            decimalSeparator={money?.local_separacion_decimales || '.'}
            prefix={`${money?.local_simbolo || 'S/'} `} decimalScale={2} fixedDecimalScale />
    )

    return (
        <div className="p-container er-report">
            {showNav && <div className="mt-3">
                <Breadcrumb routes={[
                    { id: 1, name: 'Reportes', link: '/reportes', active: false },
                    { id: 2, name: 'Flujo de efectivo', link: '/reportes/fenew', active: true },
                ]} />
            </div>}
            <header className="er-heading">
                <div>
                    <span className="er-eyebrow">Reportes financieros</span>
                    <h1 className="p-title">Flujo de efectivo</h1>
                    <p>¿Cuánto cobramos, cuánto pagamos y en qué meses los pagos superaron a los cobros?</p>
                </div>
                <div className="er-filters d-flex flex-wrap gap-2 align-items-end">
                    <div>
                        <label className="form-label" htmlFor="cash-period">Período</label>
                        <select className="form-select" id="cash-period" value={period} onChange={event => setPeriod(event.target.value)}>
                            <option value="3">Últimos 3 meses</option>
                            <option value="6">Últimos 6 meses</option>
                            <option value="year">Año en curso</option>
                        </select>
                    </div>
                    <button type="button" className="btn p-btn-secondary" disabled={!loaded} onClick={() => setRefresh(value => value + 1)}>
                        Actualizar
                    </button>
                </div>
            </header>
            {!loaded && <p role="status">Cargando flujo de efectivo…</p>}
            {loaded && !result && <p role="alert">No se pudo cargar el reporte. Intenta actualizarlo.</p>}
            {result && !months.length && <p role="status">Aún no hay meses completos en el año en curso.</p>}
            {months.length > 0 && (
                <>
                    <p className="er-source mb-3">
                        {months[0].label} a {months[months.length - 1].label} · Cobros y pagos registrados en moneda local.
                    </p>
                    <div className="row g-2 mb-3">
                        {concepts.slice(0, 3).map(({ key, label }) => (
                            <div className="col-12 col-sm-4" key={key}>
                                <div className="er-chart h-100 mb-0">
                                    <div className="er-source">{label}</div>
                                    <strong className={`fs-5 ${key === 'net' && totals.net < 0 ? 'text-danger' : ''}`}>{amount(totals[key])}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                    {lowest && <p className="er-insight">
                        Mayor salida neta en <strong>{lowest.label}</strong>. Los pagos superaron a los cobros por <strong>{amount(-lowest.net)}</strong>.
                    </p>}
                    <figure className="er-chart">
                        <figcaption className="fw-bold mb-2">Cobros, pagos y flujo neto acumulado</figcaption>
                        <div className="er-chart-scroll">
                            <ResponsiveContainer width="100%" height={280} minWidth={Math.max(380, months.length * 75)}>
                                <ComposedChart data={months}>
                                    <CartesianGrid vertical={false} stroke="#ece4ee" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis tickFormatter={value => compact.format(value)} />
                                    <Tooltip formatter={value => amount(value)} />
                                    <Legend />
                                    <ReferenceLine y={0} stroke="#675c6b" />
                                    <Bar dataKey="received" name="Cobros" fill="var(--primary-color)" maxBarSize={36} isAnimationActive={false} />
                                    <Bar dataKey="paid" name="Pagos" fill="#b45c26" maxBarSize={36} isAnimationActive={false} />
                                    <Line dataKey="accumulated" name="Flujo neto acumulado" stroke="#16755d" strokeWidth={2} isAnimationActive={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </figure>
                    <p className="er-source mb-2">El flujo acumulado parte de cero y suma los cobros menos los pagos del rango elegido.</p>
                    <div className="table-responsive er-table-wrap" tabIndex={0} role="region" aria-label="Flujo de efectivo mensual">
                        <table className="table er-table mb-0">
                            <thead><tr>
                                <th scope="col">Concepto</th>
                                {months.map(month => <th scope="col" key={month.label} className="text-end text-nowrap">{month.label}</th>)}
                                <th scope="col" className="text-end text-nowrap">Total del período</th>
                            </tr></thead>
                            <tbody>
                                {concepts.map(({ key, label }) => (
                                    <tr key={key} className={key === 'net' ? 'er-result' : ''}>
                                        <th scope="row">{label}</th>
                                        {months.map(month => (
                                            <td key={month.label} className={`text-end text-nowrap ${month[key] < 0 ? 'text-danger' : ''}`}>{amount(month[key])}</td>
                                        ))}
                                        <td className={`text-end text-nowrap er-total ${totals[key] < 0 ? 'text-danger' : ''}`}>{amount(totals[key])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    )
}
